import { useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useSearch } from "@tanstack/react-router"
import { useDebounce } from "react-use"

import type { AllowedEmailDomain } from "@productlane/zero"
import { STARTUP_PROGRAM_MAX_SEATS } from "@productlane/lib/src/browser"
import { Button } from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import type { MemberFilter } from "../member-filters"
import { MetaInfo } from "@/components/meta-info"
import { RoleCheck } from "@/components/shared/role-check"
import { api } from "@/lib/api"
import { useRole } from "@/lib/useRole"
import { SettingsContainer } from "@/screens/settings/settings-switches/settings-container"
import { AddEmailDomainModal } from "../add-email-domain-modal"
import { AllowedDomainItem } from "../allowed-domain-item"
import { InviteItem } from "../invite-item"
import { InviteMemberModal } from "../invite-member-modal"
import { filterInvites, filterMemberships } from "../member-filters"
import { MemberItem } from "../member-item"
import { MemberRoleFilter } from "../member-role-filter"
import { MemberSearch } from "../member-search"
import { SettingsHeader } from "../settings-header"
import { VerifyEmailDomainModal } from "../verify-email-domain.modal"

export function MemberSettingsPage() {
	const [inviteModalOpen, setInviteModalOpen] = useState(false)
	const [addEmailDomainModalOpen, setAddEmailDomainModalOpen] = useState(false)
	const { role } = useRole()
	const highlightUserId = useSearch({
		strict: false,
		select: (s) => s.highlight,
	})

	const [search, setSearch] = useState("")
	const [debouncedSearch, setDebouncedSearch] = useState("")
	useDebounce(() => setDebouncedSearch(search), 200, [search])
	const [roleFilter, setRoleFilter] = useState<MemberFilter>("all")

	const [memberships] = useQuery(queries.memberships())
	const [invites] = useQuery(queries.invitedUsers())
	const [allowedEmailDomains] = useQuery(queries.allowedDomains())

	const { data: trialInfo } = api.workspace.trialInfo.useQuery(undefined, {
		cacheTime: Infinity,
		staleTime: Infinity,
	})
	const seatsUsed =
		memberships.filter((m) => m.role !== "VIEWER").length +
		invites.filter((i) => i.role !== "VIEWER").length

	const filteredMemberships = filterMemberships(
		memberships,
		roleFilter,
		debouncedSearch,
	)
	const filteredInvites = filterInvites(invites, roleFilter, debouncedSearch)
	const hasMembersOrInvites = !!memberships.length || !!invites.length
	const noFilteredResults =
		hasMembersOrInvites &&
		!filteredMemberships.length &&
		!filteredInvites.length

	const [
		allowedEmailDomainVerificationData,
		setAllowedEmailDomainVerificationData,
	] = useState<Omit<AllowedEmailDomain, "hashedVerificationToken"> | null>(null)

	return (
		<div className="space-y-4">
			<MetaInfo title="Members" />

			<SettingsHeader
				title="Members"
				subtitle={
					role === "ADMIN"
						? "Manage who has access to Productlane."
						: "View who has access to Productlane."
				}
				docsArticle="12c19a75-1c72-40b2-ae26-c22300eb3203"
			/>
			<SettingsContainer>
				<div className="text-sm">
					<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
						<div>
							<p className="font-medium">Workspace members</p>
							<p className="mt-1 max-w-prose text-secondary">
								These are the members that have access to the workspace.
							</p>
							{trialInfo?.isStartupProgram === true && (
								<p className="mt-1 max-w-prose text-xs text-tertiary">
									Startup program: {seatsUsed}/{STARTUP_PROGRAM_MAX_SEATS} seats
									used. Viewers are free.
								</p>
							)}
						</div>
						<RoleCheck only={["ADMIN"]}>
							<Button
								className="hidden sm:flex"
								onClick={() => setInviteModalOpen(true)}
							>
								Invite member
							</Button>
						</RoleCheck>
					</div>
					{hasMembersOrInvites && (
						<div className="mt-4 flex items-center gap-2">
							<MemberSearch
								value={search}
								onChange={(value) => {
									setSearch(value)
									if (!value) setDebouncedSearch("")
								}}
							/>
							<MemberRoleFilter
								value={roleFilter}
								onChange={setRoleFilter}
								includePending={role === "ADMIN"}
							/>
						</div>
					)}
					<div className="mt-2 space-y-2">
						{filteredMemberships.map((m) => (
							<MemberItem
								key={m.id}
								membership={
									m as typeof m & { user: NonNullable<typeof m.user> }
								}
								highlighted={highlightUserId === m.user?.id}
							/>
						))}
					</div>
					{!!filteredInvites.length && role === "ADMIN" && (
						<div className="pt-6">
							<div>
								<p className="font-medium">Workspace invites</p>
								<p className="mt-1 text-secondary">
									People invited to join your workspace.
								</p>
							</div>
							<div className="mt-2 space-y-2">
								{filteredInvites.map((i) => (
									<InviteItem key={i.id} invite={i} />
								))}
							</div>
						</div>
					)}
					{noFilteredResults && (
						<p className="mt-4 text-secondary">No members match your search.</p>
					)}
					<RoleCheck only={["ADMIN"]}>
						<div className="mt-6 w-full sm:hidden">
							<Button
								className="w-full max-w-lg py-4"
								onClick={() => setInviteModalOpen(true)}
							>
								Invite member
							</Button>
						</div>
					</RoleCheck>
				</div>
			</SettingsContainer>
			<SettingsContainer>
				<div className="space-y-4">
					<div className="text-sm">
						<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
							<div>
								<p className="font-medium">Allowed Domains</p>
								<p className="mt-1 max-w-prose text-secondary">
									Anyone with an email ending in one of these domains can join
									the workspace.
								</p>
							</div>
							<RoleCheck only={["ADMIN"]}>
								<Button
									className="hidden sm:flex"
									onClick={() => setAddEmailDomainModalOpen(true)}
								>
									Add domain
								</Button>
							</RoleCheck>
						</div>
					</div>
					{!!allowedEmailDomains.length &&
						allowedEmailDomains.map((d) => (
							<AllowedDomainItem
								key={d.id}
								allowedDomain={d}
								onVerify={(domain) =>
									setAllowedEmailDomainVerificationData(domain)
								}
							/>
						))}
					<RoleCheck only={["ADMIN"]}>
						<div className="-mx-4 mt-6 px-4 sm:hidden">
							<Button
								className="w-full max-w-lg py-4"
								onClick={() => setAddEmailDomainModalOpen(true)}
							>
								Add domain
							</Button>
						</div>
					</RoleCheck>
				</div>
			</SettingsContainer>

			<RoleCheck only={["ADMIN"]}>
				<InviteMemberModal
					open={inviteModalOpen}
					onClose={() => setInviteModalOpen(false)}
				/>

				<AddEmailDomainModal
					open={addEmailDomainModalOpen}
					onClose={(data) => {
						setAddEmailDomainModalOpen(false)
						if (data) {
							setAllowedEmailDomainVerificationData({
								...data,
								createdAt: data.createdAt.getTime(),
								updatedAt: data.updatedAt.getTime(),
								verificationEmailSentAt:
									data.verificationEmailSentAt?.getTime() ?? null,
								verifiedAt: data.verifiedAt?.getTime() ?? null,
							})
						}
					}}
				/>
				<VerifyEmailDomainModal
					open={!!allowedEmailDomainVerificationData}
					onClose={() => setAllowedEmailDomainVerificationData(null)}
					data={
						allowedEmailDomainVerificationData
							? {
									...allowedEmailDomainVerificationData,
									createdAt: new Date(
										allowedEmailDomainVerificationData.createdAt,
									),
									updatedAt: new Date(
										allowedEmailDomainVerificationData.updatedAt,
									),
									verificationEmailSentAt:
										allowedEmailDomainVerificationData.verificationEmailSentAt
											? new Date(
													allowedEmailDomainVerificationData.verificationEmailSentAt,
												)
											: null,
									verifiedAt: allowedEmailDomainVerificationData.verifiedAt
										? new Date(allowedEmailDomainVerificationData.verifiedAt)
										: null,
								}
							: null
					}
				/>
			</RoleCheck>
		</div>
	)
}
